import { Alert, Box, Button, Stack, Tab, Tabs, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import Breadcrumb from '@/components/shared/Breadcrumb';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useUpdateOrganization } from '@/hooks/useUpdateOrganization';
import { ROUTES } from '@/routes';
import { handleSnackbarError } from '@/shared/utils/errorHandler';
import { useTheme } from '@/theme/useTheme';

import { FormErrors, FormValues, OrganizationFormFields } from '../components/OrganizationFormFields';
import { useOrganizationById } from '../hooks/useOrganizationById';
import SystemAccessTab from '../SystemAccess/SystemAccessTab';
import UserAccessTab from '../UserAccess';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div role='tabpanel' hidden={value !== index} id={`vertical-tabpanel-${index}`} aria-labelledby={`vertical-tab-${index}`} {...other}>
      {value === index && <Box>{children}</Box>}
    </div>
  );
}

function allProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

function OrganizationDetails() {
  const { id } = useParams<{ id: string }>();
  const { organization, isLoading, error } = useOrganizationById(Number(id));
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const theme = useTheme();

  const { updateOrganization, isPending, validationErrors } = useUpdateOrganization();
  const snackbar = useSnackbar();

  const [overrides, setOverrides] = useState<Partial<FormValues>>({});

  const values: FormValues = {
    displayName: overrides.displayName ?? organization?.name ?? '',
    organizationCode: overrides.organizationCode ?? organization?.organizationCode ?? '',
    organizationClaim: overrides.organizationClaim ?? organization?.organizationClaim ?? '',
    logoUrl: overrides.logoUrl ?? organization?.logoUrl ?? '',
    isActive: overrides.isActive ?? organization?.status === 'Active',
  };

  const getTab = (params: URLSearchParams) => {
    const tab = parseInt(params.get('tab') || '0', 10);
    return isNaN(tab) || tab < 0 || tab > 3 ? 0 : tab;
  };

  const currentTab = getTab(searchParams);

  const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
    const params = new URLSearchParams(searchParams);
    if (newValue === 0) params.delete('tab');
    else params.set('tab', newValue.toString());
    setSearchParams(params, { replace: true });
  };

  const location = useLocation();
  const breadcrumbPath = useMemo(() => {
    const state = location.state as { returnSearchParams?: string } | undefined;
    const returnSearchParams = state?.returnSearchParams;

    if (returnSearchParams) {
      return `${ROUTES.ADMIN_ORGANIZATION}?${returnSearchParams}`;
    }
    return ROUTES.ADMIN_ORGANIZATION;
  }, [location.state]);

  const breadcrumbItems = [
    { label: 'Manage Clients for Account Admin', path: breadcrumbPath },
    {
      label: 'Manage Client',
      path: `/organizations/${id}`,
    },
  ];

  const handleFieldChange = (field: keyof Omit<FormValues, 'isActive'>) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setOverrides((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleFieldClear = (field: keyof Omit<FormValues, 'isActive'>) => {
    setOverrides((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCancel = () => {
    setOverrides({});
    navigate(breadcrumbPath);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      await updateOrganization({
        id: Number(id),
        name: values.displayName,
        organizationCode: values.organizationCode,
        organizationClaim: values.organizationClaim,
        logoUrl: values.logoUrl,
        status: values.isActive ? 'Active' : 'Inactive',
      });

      snackbar.showSuccess('Client updated successfully');
    } catch (err) {
      handleSnackbarError(err, snackbar);
    }
  };

  const fieldErrors: FormErrors = {
    displayName: validationErrors?.['Name']?.[0],
    organizationCode: validationErrors?.['OrganizationCode']?.[0],
    organizationClaim: validationErrors?.['OrganizationClaim']?.[0],
    logoUrl: validationErrors?.['LogoUrl']?.[0],
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Breadcrumb items={breadcrumbItems} />
      <Typography
        data-testid='organization-details-title'
        sx={{
          fontSize: theme.hhglobal.typography.headings.h1.fontSize,
          lineHeight: theme.hhglobal.typography.headings.h1.lineHeight,
          marginBottom: theme.hhglobal.spacing.spacing[2].valuePx,
          color: theme.hhglobal.neutral.superblack,
        }}
      >
        {organization?.name || ''}
      </Typography>
      <Box sx={{ display: 'flex', gap: theme.hhglobal.spacing.spacing[7].valuePx, width: '100%' }}>
        <Tabs
          orientation='vertical'
          variant='scrollable'
          value={currentTab}
          onChange={handleChange}
          aria-label='Organization details tabs'
          data-testid='organization-details-tabs'
          sx={{
            minWidth: 200,
            '& .MuiTabs-indicator': {
              left: 0,
              right: 'auto',
              backgroundColor: '#000000',
            },
            '& .MuiTab-root': {
              textTransform: 'none',
              textAlign: 'left',
              alignItems: 'flex-start',
              justifyContent: 'flex-start',
              color: '#000000',
              minHeight: '40px',
              padding: '12px',
              fontSize: '14px',
              fontWeight: 400,
            },
            '& .MuiTab-root.Mui-selected': {
              fontWeight: 700,
              color: '#000000',
            },
          }}
        >
          <Tab label='Client Information' {...allProps(0)} data-testid='tab-organization-information' />
          <Tab label='Account Admin Access' {...allProps(1)} data-testid='tab-account-admin-access' />
          <Tab label='System Access' {...allProps(2)} data-testid='tab-system-access' />
        </Tabs>
        <Box sx={{ flexGrow: 1 }}>
          <TabPanel value={currentTab} index={0}>
            {isLoading && <div>Loading...</div>}
            {error && (
              <Alert severity='error' sx={{ display: 'flex', alignItems: 'center', fontSize: '14px', lineHeight: '20px' }}>
                Error: {error.message}
              </Alert>
            )}
            {organization && (
              <Box
                component='form'
                noValidate
                autoComplete='off'
                onSubmit={handleSubmit}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: theme.hhglobal.spacing.spacing[7].valuePx,
                }}
              >
                <OrganizationFormFields
                  values={values}
                  fieldErrors={fieldErrors}
                  onChange={handleFieldChange}
                  onClear={handleFieldClear}
                  onToggleStatus={(checked) => setOverrides((prev) => ({ ...prev, isActive: checked }))}
                  idPrefix='manage-org'
                />

                <Stack direction='row' spacing={theme.hhglobal.spacing.spacing[4].valuePx}>
                  <Button
                    variant='outlined'
                    data-testid='cancel-button'
                    onClick={handleCancel}
                    sx={{
                      height: '36px',
                      fontSize: '14px',
                      boxShadow: 'none',
                      textTransform: 'none',
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    variant='contained'
                    disabled={isPending}
                    data-testid='save-client-button'
                    sx={{
                      height: '36px',
                      fontSize: '14px',
                      boxShadow: 'none',
                      textTransform: 'none',
                      fontWeight: theme.hhglobal.typography.fontWeights.regular,
                      '&:active': {
                        backgroundColor: theme.hhglobal.neutral.gray900,
                        color: theme.palette.common.white,
                      },
                    }}
                  >
                    {isPending ? 'Saving...' : 'Save Client'}
                  </Button>
                </Stack>
              </Box>
            )}
          </TabPanel>
          <TabPanel value={currentTab} index={1}>
            <UserAccessTab organizationId={organization?.id} />
          </TabPanel>
          <TabPanel value={currentTab} index={2}>
            <SystemAccessTab />
          </TabPanel>
        </Box>
      </Box>
    </Box>
  );
}

export default OrganizationDetails;
